import { chain, merge } from 'lodash';

import React from 'react';
import TemplateText from './TemplateText';
import {
    removeText,
    setCursorAt,
    getCursorPositionData,
} from '../../../../widgets/rich-text-editor/TextEditorUtils';
import {
    DefaultOpenTemplateTextChar,
    DefaultCloseTemplateTextChar,
    TextTemplateValues,
    ActionsTemplateValues,
    TemplateTextDynamic,
} from './TemplateTextConsts';
import UtilsService from '../../../../services/UtilsService';
import TemplateAction from './TemplateAction';
import TemplateDynamicTag from './TemplateDynamicTag';

export const emailTemplatePluginDefaultConfig = {
    openTemplateTextChar: DefaultOpenTemplateTextChar,
    closeTemplateTextChar: DefaultCloseTemplateTextChar,
    templateTextContextData: {},
};

const createStringByLength = (len, letter = 'x') => letter.repeat(len);
const replaceTextByIndex = (text, start, end, value) => text.slice(0, start) + value + text.slice(end);

export const isCursorOnTemplateText = (blockText, charIndex, openTemplateTextChar, closeTemplateTextChar) => {
    if (blockText.length < 3) {
        return {
            isTemplateText: false,
        };
    }

    const openCharEscaped = UtilsService.escapeRegExp(openTemplateTextChar);
    const closeCharEscaped = UtilsService.escapeRegExp(closeTemplateTextChar);

    const validTemplates = chain({ ...TextTemplateValues, ...ActionsTemplateValues, ...TemplateTextDynamic })
        .values()
        .join('|')
        .value();

    const regex = new RegExp(`${openCharEscaped}(${validTemplates})${closeCharEscaped}`, 'g');
    const allTemplateStrings = blockText.match(regex);
    let checkedBlockText = blockText;

    for (const templateString of (allTemplateStrings || [])) {
        const start = checkedBlockText.indexOf(templateString);
        const end = start + templateString.length;
        const isTemplateBlockChar = charIndex > start && charIndex < end;
        checkedBlockText = replaceTextByIndex(checkedBlockText, start, end, createStringByLength(templateString.length));
        if (isTemplateBlockChar) {
            return {
                isTemplateText: true,
                start,
                end,
            };
        }
    }

    return {
        isTemplateText: false,
    };
};

export const getTemplateStrategy = ({ values }) => ({ openTemplateTextChar, closeTemplateTextChar }) => (contentBlock, callback) => {
    const blockText = contentBlock.getText();

    const openCharEscaped = UtilsService.escapeRegExp(openTemplateTextChar);
    const closeCharEscaped = UtilsService.escapeRegExp(closeTemplateTextChar);

    const validTemplates = chain(values)
        .values()
        .join('|')
        .value();

    const regex = new RegExp(`${openCharEscaped}(${validTemplates})${closeCharEscaped}`, 'g');

    const allTemplateStrings = blockText.match(regex);
    let checkedBlockText = blockText;
    for (const templateString of (allTemplateStrings || [])) {
        const start = checkedBlockText.indexOf(templateString);
        const end = start + templateString.length;
        checkedBlockText = replaceTextByIndex(checkedBlockText, start, end, createStringByLength(templateString.length));
        callback(start, end);
    }
};

export const getTemplateTextStrategy = getTemplateStrategy({ values: TextTemplateValues });
export const getTemplateActionsStrategy = getTemplateStrategy({ values: ActionsTemplateValues });
export const getTemplateDynamicStrategy = getTemplateStrategy({ values: TemplateTextDynamic });

const createEmailTemplatePlugin = (inputConfig = {}, isDynamicDisabled = false) => {
    const resultConfig = merge({}, emailTemplatePluginDefaultConfig, inputConfig);

    const {
        openTemplateTextChar,
        closeTemplateTextChar,
        templateTextContextData,
        isActionButtonDisabled,
    } = resultConfig;

    const store = {
        getEditorState: undefined,
        setEditorState: undefined,
        isTemplateTextSuggestionsOpen: false,
        onTemplateTextSuggestionsOpenStateChange(newState) {
            this.isTemplateTextSuggestionsOpen = newState;
        },
    };

    const onTemplateTextRemove = (params) => {
        const {
            blockKey,
            start: startTextTemplateIndex,
            end: endTemplateIndex,
        } = params;

        const editorState = store.getEditorState();
        const updateEditorState = removeText(editorState, blockKey, startTextTemplateIndex, endTemplateIndex);

        store.setEditorState(updateEditorState);
    };

    return {
        initialize: ({ getEditorState, setEditorState }) => {
            store.getEditorState = getEditorState;
            store.setEditorState = setEditorState;
        },
        onFocus: (e, { getEditorState, setEditorState }) => {
            store.getEditorState = getEditorState;
            store.setEditorState = setEditorState;
        },
        decorators: [{
            strategy: getTemplateTextStrategy({ openTemplateTextChar, closeTemplateTextChar }),
            component: TemplateText,
            props: {
                editable: false,
                openTemplateTextChar,
                closeTemplateTextChar,
                templateTextContextData,
                onRemove: onTemplateTextRemove,
            },
        }, {
            strategy: getTemplateActionsStrategy({ openTemplateTextChar, closeTemplateTextChar }),
            component: TemplateAction,
            props: {
                editable: false,
                openTemplateTextChar,
                closeTemplateTextChar,
                templateTextContextData,
                onRemove: onTemplateTextRemove,
                isActionButtonDisabled,
            },
        }, {
            strategy: getTemplateDynamicStrategy({ openTemplateTextChar, closeTemplateTextChar }),
            component: TemplateDynamicTag,
            props: {
                isDynamicDisabled,
                editable: false,
                openTemplateTextChar,
                closeTemplateTextChar,
                templateTextContextData,
                onRemove: onTemplateTextRemove,
            },
        },
        ],
        onLeftArrow(e, pluginFunctions) {
            const editorState = pluginFunctions.getEditorState();
            const { start, text, anchorKey } = getCursorPositionData(editorState);
            const checkResult = isCursorOnTemplateText(text, start - 1, openTemplateTextChar, closeTemplateTextChar);

            if (checkResult.isTemplateText) {
                const newEditorState = setCursorAt(editorState, anchorKey, checkResult.start + 1);

                pluginFunctions.setEditorState(newEditorState);

                return 'handled';
            }

            return 'not-handled';
        },
        onRightArrow(e, pluginFunctions) {
            const editorState = pluginFunctions.getEditorState();
            const { start, text, anchorKey } = getCursorPositionData(editorState);
            const checkResult = isCursorOnTemplateText(text, start + 1, openTemplateTextChar, closeTemplateTextChar);

            if (checkResult.isTemplateText) {
                const newEditorState = setCursorAt(editorState, anchorKey, checkResult.end);

                pluginFunctions.setEditorState(newEditorState);

                return 'handled';
            }

            return 'not-handled';
        },
        handleKeyCommand(command, editorState, eventTimeStamp, pluginFunctions) {
            const isBackspace = command === 'backspace';
            const isDelete = command === 'delete';
            if (isBackspace || isDelete) {
                const { start, text, anchorKey } = getCursorPositionData(editorState);
                const cursorShiftForPositionCheck = isBackspace ? -1 : 1;
                const cursorPositionWithShift = start + cursorShiftForPositionCheck;

                const checkResult = isCursorOnTemplateText(text, cursorPositionWithShift, openTemplateTextChar, closeTemplateTextChar);

                if (checkResult.isTemplateText) {
                    const updateEditorState = removeText(editorState, anchorKey, checkResult.start, checkResult.end);

                    pluginFunctions.setEditorState(updateEditorState);

                    return 'handled';
                }
            }

            return 'not-handled';
        },
    };
};

export default createEmailTemplatePlugin;
